import React from 'react';
import { Button, Select, Checkbox } from '@crisellevaldez/speculo-ui';
import { Loader2 } from 'lucide-react';
import { TablePaginationProps } from '../../types/contact.types';
import { formatNumber } from '../../utils/format';

export const TablePagination: React.FC<TablePaginationProps> = ({
  hasNext,
  onNextPage,
  totalItems: _totalItems,
  resultsCount,
  isLoading,
  currentItems,
  pageSize = 25,
  onPageSizeChange,
  showAllRecordsOption = false,
  showAllRecords = false,
  onShowAllRecordsChange,
}) => {
  // Only render the pagination component if there are more records to show
  // or if there's count information to display
  // or if showAllRecordsOption is true
  const shouldShowCountInfo = currentItems !== undefined;

  if (!hasNext && !shouldShowCountInfo && !showAllRecordsOption) {
    return null; // Don't render anything if there's nothing to show
  }

  // Page size options
  const pageSizeOptions = [
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '500', label: '500' },
  ];

  return (
    <div className="flex justify-between items-center px-4 py-1.5">
      {/* Show count information based on available props */}
      <div className="text-sm text-muted-foreground text-left flex items-center">
        {showAllRecordsOption && (!showAllRecords || isLoading) && (
          <div className="flex items-center mr-4">
            <Checkbox
              id="show-all-records"
              checked={showAllRecords}
              disabled={isLoading}
              onChange={(e) => onShowAllRecordsChange && onShowAllRecordsChange(e.target.checked)}
            />
            <label htmlFor="show-all-records" className="ml-2 text-sm text-foreground cursor-pointer">
              Show all records
              {isLoading && showAllRecords && (
                <Loader2 className="ml-2 h-3 w-3 inline-block animate-spin text-muted-foreground" />
              )}
            </label>
          </div>
        )}
        {shouldShowCountInfo && resultsCount !== undefined && (
          // Only show count when resultsCount is defined (not firstName/lastName search)
          <div className="whitespace-pre">
            Showing <span className="font-bold">{formatNumber(currentItems)}</span> out of{' '}
            <span className="font-bold">{formatNumber(resultsCount)}</span>
          </div>
        )}
      </div>

      {/* See More button in the middle */}
      <div className="flex-1 flex justify-center">
        {hasNext && !showAllRecords && (
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'See More'
            )}
          </Button>
        )}
      </div>

      {/* Page size selector on the right */}
      <div className="text-sm">
        {onPageSizeChange && (
          <div className="flex items-center whitespace-nowrap">
            <Select
              options={pageSizeOptions}
              value={String(pageSize)}
              onChange={(value) => onPageSizeChange(Number(value))}
              className="w-25"
            />
            <span className="text-muted-foreground ml-2 inline-block">per page</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TablePagination;
